import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { consultingCopy as copy } from '../data/consultingCopy';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import { submitInquiry } from '../utils/submitInquiry';
import '../styles/Consulting.css';

const Consulting = () => {
  usePageMeta({
    title: '컨설팅 신청',
    description:
      '일본 학부 입시 루트 90분 컨설팅. PayPal 또는 계좌 · 일정 확정 후 메일 안내. 합격 보장 없음.',
    path: '/consulting',
  });
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    track: 'eju',
    field: 'stem',
    stage: 'explore',
    education: '',
    english: '',
    ejuStatus: '',
    goals: '',
    status: '',
    questions: '',
    schedule: '',
    website: '', // honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setCopied(false);
  };

  const buildMailBody = useMemo(() => {
    const trackLabel = { eju: 'EJU 유학생', general: '일반입시', mext: '국비(MEXT)' }[formData.track];
    const fieldLabel = {
      stem: '이공·정보',
      medicine: '의치약·보건',
      humanities: '문과·사회',
    }[formData.field];
    const stageLabel = {
      explore: '탐색 중',
      prep: '본격 준비',
      apply: '지원 직전',
    }[formData.stage];

    return [
      '[미라이쥬쿠 컨설팅 신청]',
      `이름: ${formData.name || '—'}`,
      `이메일: ${formData.email || '—'}`,
      `전형: ${trackLabel}`,
      `계열: ${fieldLabel}`,
      `단계: ${stageLabel}`,
      '',
      '학력(12년 등):',
      formData.education || '-',
      '',
      '영어 성적·준비:',
      formData.english || '-',
      '',
      'EJU·일본어:',
      formData.ejuStatus || '-',
      '',
      '목표 대학·학부:',
      formData.goals || '-',
      '',
      '현재 준비 상태:',
      formData.status || '-',
      '',
      '궁금한 점:',
      formData.questions || '-',
      '',
      '희망 일정:',
      formData.schedule || '-',
      '',
      '상품: 90분 · 400,000원',
      '결제: PayPal 또는 계좌 이체 (일정 확정 후 메일 안내)',
    ].join('\n');
  }, [formData]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildMailBody);
      setCopied(true);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('복사에 실패했습니다. 아래 미리보기에서 직접 선택해 복사해 주세요.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return; // bot

    setIsSubmitting(true);
    setSuccess(null);
    setError(null);

    const subject = `[미라이쥬쿠 컨설팅] ${formData.name}`;

    try {
      await submitInquiry({
        name: formData.name,
        email: formData.email,
        subject,
        message: buildMailBody,
        honey: formData.website,
        extra: { kind: 'consulting' },
      });

      setSuccess(
        '신청이 전달되었습니다. 받은편지함(또는 스팸)에서 확인되면 2~3영업일 내 회신합니다. 해당 월 일정이 가득하면 대기·다음 달 일정을 안내합니다.'
      );
      trackEvent(AnalyticsEvents.CONSULT_SUBMIT);
    } catch (err) {
      console.error(err);
      setError(
        '자동 전송에 실패했습니다. 아래 완성 문구를 복사해 메일로 직접 보내 주세요.'
      );
      try {
        await navigator.clipboard.writeText(buildMailBody);
        setCopied(true);
      } catch (_) {
        /* preview still visible */
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="apple-page apple-consulting">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">{copy.eyebrow}</p>
        <h1 className="apple-display">{copy.title}</h1>
        <p className="apple-lede">{copy.lede}</p>
      </header>

      <section className="apple-consult-price" aria-label="가격">
        <p className="apple-consult-price-main">{copy.priceLabel}</p>
        <p className="apple-consult-price-note">{copy.priceNote}</p>
        {copy.capacityNote && (
          <p className="apple-consult-price-note">{copy.capacityNote}</p>
        )}
      </section>

      <div className="apple-consult-grid">
        <section>
          <h2>포함</h2>
          <ul>
            {copy.includes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>미포함</h2>
          <ul>
            {copy.excludes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>준비하면 좋은 것</h2>
          <ul>
            {copy.prepare.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>진행</h2>
          <ol>
            {copy.process.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </section>
        <section>
          <h2>취소·환불</h2>
          <ul>
            {copy.policy.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="apple-consult-policy-link">
            <Link to="/refund">환불·결제 정책 전문</Link>
          </p>
        </section>
      </div>

      <section className="apple-consult-form-wrap">
        <h2>신청</h2>
        <p className="apple-consult-form-intro">{copy.formIntro}</p>

        {success && <div className="apple-consult-banner is-ok">{success}</div>}
        {error && <div className="apple-consult-banner is-err">{error}</div>}

        <form className="apple-consult-form" onSubmit={handleSubmit}>
          <div className="apple-consult-honeypot" aria-hidden="true">
            <label>
              website
              <input
                tabIndex={-1}
                autoComplete="off"
                name="website"
                value={formData.website}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="apple-consult-row">
            <label>
              이름 *
              <input name="name" value={formData.name} onChange={handleChange} required />
            </label>
            <label>
              이메일 *
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div className="apple-consult-row apple-consult-row-3">
            <label>
              관심 전형 *
              <select name="track" value={formData.track} onChange={handleChange}>
                <option value="eju">EJU 유학생</option>
                <option value="general">일반입시</option>
                <option value="mext">국비(MEXT)</option>
              </select>
            </label>
            <label>
              계열 *
              <select name="field" value={formData.field} onChange={handleChange}>
                <option value="stem">이공·정보</option>
                <option value="medicine">의치약·보건</option>
                <option value="humanities">문과·사회</option>
              </select>
            </label>
            <label>
              단계 *
              <select name="stage" value={formData.stage} onChange={handleChange}>
                <option value="explore">탐색 중</option>
                <option value="prep">본격 준비</option>
                <option value="apply">지원 직전</option>
              </select>
            </label>
          </div>

          <fieldset className="apple-consult-screen">
            <legend>사전 스크리닝</legend>
            <label>
              학력 *
              <textarea
                name="education"
                value={formData.education}
                onChange={handleChange}
                rows={2}
                required
                placeholder="예: 한국 고교 졸업(예정) / 검정고시 / 대학 재학·휴학 등. 12년 학력 여부"
              />
            </label>
            <label>
              영어 *
              <textarea
                name="english"
                value={formData.english}
                onChange={handleChange}
                rows={2}
                required
                placeholder="TOEFL/IELTS/TOEIC 점수·시험일, 또는 아직 미응시·준비 중"
              />
            </label>
            <label>
              EJU·일본어 *
              <textarea
                name="ejuStatus"
                value={formData.ejuStatus}
                onChange={handleChange}
                rows={2}
                required
                placeholder="EJU 응시·예정 회차/과목, JLPT 등 (없으면 ‘미정’)"
              />
            </label>
          </fieldset>

          <label>
            목표 대학·학부
            <textarea
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              rows={3}
              placeholder="예: ○○대 공학부 / 아직 미정"
            />
          </label>

          <label>
            기타 준비 상태
            <textarea
              name="status"
              value={formData.status}
              onChange={handleChange}
              rows={2}
              placeholder="서류·일정·병행 전형 등 (선택)"
            />
          </label>

          <label>
            궁금한 점 *
            <textarea
              name="questions"
              value={formData.questions}
              onChange={handleChange}
              rows={4}
              required
              placeholder="세션에서 꼭 다루고 싶은 질문"
            />
          </label>

          <label>
            희망 일정
            <textarea
              name="schedule"
              value={formData.schedule}
              onChange={handleChange}
              rows={2}
              placeholder="가능한 요일·시간대 (한국/일본 시간)"
            />
          </label>

          <div className="apple-consult-actions">
            <button type="submit" className="apple-btn apple-btn-primary" disabled={isSubmitting}>
              {isSubmitting ? '전송 중…' : '신청 보내기'}
            </button>
            <button
              type="button"
              className="apple-btn apple-btn-secondary"
              onClick={handleCopy}
            >
              {copied ? '복사됨' : '문구 복사'}
            </button>
          </div>
        </form>

        <div className="apple-consult-preview">
          <div className="apple-consult-preview-head">
            <h3>완성 문구 미리보기</h3>
            <p>자동 전송이 안 될 때, 복사해서 메일로 붙여 넣으면 됩니다.</p>
          </div>
          <pre>{buildMailBody}</pre>
        </div>
      </section>

      <p className="apple-consult-foot-note">
        일정·결제 확인: <ObfuscatedEmail />
      </p>
      <p className="apple-consult-disclaimer">{copy.disclaimer}</p>

      <div className="apple-page-foot">
        <Link to="/finder" className="apple-link">루트 찾기</Link>
        <Link to="/resources" className="apple-link">공식 자료</Link>
      </div>
    </div>
  );
};

export default Consulting;
